import './index.css'
import {
  ADDON_STORAGE,
  STORAGE,
  USER,
} from '@disco/constants/billing-provider-components'
import { getDefaultPlanQuotas } from '@disco/utils/pricing'

import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { Link } from 'react-router-dom'
import { loadBillingProviderComponents } from '@disco/state-management/actions/billing-provider-components'
import { seatsAndTracksUpdate } from '@disco/state-management/actions/SeatsAndTracks'
import { getSubscriptionPeriod } from '@disco/state-management/selectors/subscription'
import { getCorePlan } from '@disco/state-management/selectors/user'
import Alert from 'components/ui/alert'
import ContactUsLink from 'components/ui/contact-us-link'
import CustomDropdown from 'components/ui/dropdowns/custom-dropdown'
import Heading from 'components/ui/heading'
import Icon from 'components/ui/icon'
import Label from 'components/ui/label'
import ModalDialog from 'components/ui/modal-dialog'
import ModalWindow from 'components/ui/modal-window'
import Spinner from 'components/ui/spinner'
import Stack from 'components/ui/stack'
import Text from 'components/ui/text'
import {
  useDynamicTracksOptions,
  useDynamicUserOptions,
} from '../dynamicPricingOptions'
import OverQuotaAlert from './over-quota-alert'
import { SeatsAndTracksChargebeePricingChange } from './seats-and-tracks-chargebee-pricing-change/SeatsAndTracksChargebeePricingChange'

type Props = {
  enterprise_track_allowance: number
  enterprise_user_allowance: number
  number_of_users: number
  total_track_count: number
  currentBusinessPlan: {
    id: string
    name: string
  }
  onClose: (...args: Array<any>) => any
  usersLoading: boolean
}

const SeatsAndTracks = ({
  enterprise_track_allowance,
  enterprise_user_allowance,
  number_of_users,
  total_track_count,
  currentBusinessPlan,
  onClose,
  usersLoading,
}: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const tracksDynamicOptions = useDynamicTracksOptions()
  const usersDynamicOptions = useDynamicUserOptions()
  const corePlan = useSelector(getCorePlan)
  const subscriptionPeriod = useSelector(getSubscriptionPeriod)
  const defaultPlanQuotas = getDefaultPlanQuotas(corePlan?.id)
  const [currentUserAllowance, setCurrentUserAllowance] = useState(
    enterprise_user_allowance - defaultPlanQuotas.users,
  )
  const [currentTrackAllowance, setCurrentTrackAllowance] = useState(
    enterprise_track_allowance - defaultPlanQuotas.files,
  )
  const [userAllowance, setUserAllowance] = useState({
    value: currentUserAllowance,
    type: USER,
    component_id: 0,
    isUpgrade: false,
    amount: 0,
  })
  const [trackAllowance, setTrackAllowance] = useState({
    value: currentTrackAllowance,
    type: STORAGE,
    component_id: 0,
    isUpgrade: false,
    amount: 0,
  })
  const [updatingAllocateComponent, setUpdatingAllocateComponent] =
    useState(false)
  const [paymentFailed, setPaymentFailed] = useState(false)
  const [successModal, setSuccessModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [usersOptions, setUsersOptions] = useState(usersDynamicOptions.options)
  const [tracksOptions, setTracksOptions] = useState(
    tracksDynamicOptions.options,
  )
  const billingProviderComponents = useSelector(
    state => state.billingProviderComponents,
  )
  useEffect(() => {
    setUsersOptions(usersDynamicOptions.options)
    setTracksOptions(tracksDynamicOptions.options)

    if (tracksDynamicOptions.loaded && usersDynamicOptions.loaded) {
      setLoading(false)
    } else {
      setLoading(true)
    }
  }, [tracksDynamicOptions, usersDynamicOptions])
  useEffect(() => {
    dispatch(loadBillingProviderComponents())
  }, [])
  const hasUnsavedChanges =
    trackAllowance.value !== currentTrackAllowance ||
    userAllowance.value !== currentUserAllowance
  const updateBillingInfo = (
    <Link
      className="SeatsAndTracks_UpdateBillingInfo"
      to="/settings/billing/update"
    >
      Update billing info
    </Link>
  )

  if (successModal) {
    const isUpdatedUser = userAllowance.value !== currentUserAllowance
    const isUpdatedTrack = trackAllowance.value !== currentTrackAllowance
    const userValue = userAllowance.value + defaultPlanQuotas.users
    const trackValue = trackAllowance.value + defaultPlanQuotas.files
    return (
      <ModalWindow
        header={
          <Heading size={600}>
            <Icon type="check" className="SeatsAndTracks_SuccessIcon" />
            Plan Successfully Updated
          </Heading>
        }
        width="small"
        isOpen
        onClose={onClose}
      >
        <Text type="paragraph">
          <span>
            You’ve updated your plan subscription to{' '}
            <span>
              {isUpdatedUser
                ? `${userValue} ${userValue > 1 ? 'users' : 'user'}`
                : ''}
            </span>{' '}
          </span>
          {isUpdatedUser && isUpdatedTrack ? <span>and</span> : null}{' '}
          <span>{isUpdatedTrack ? `${trackValue} tracks` : ''}</span>
        </Text>
      </ModalWindow>
    )
  }

  const onUpdatePlan = async () => {
    try {
      setUpdatingAllocateComponent(true)
      await dispatch(
        seatsAndTracksUpdate({
          items: [
            {
              newValue: trackAllowance.value,
              component_id: trackAllowance.component_id,
              type: STORAGE,
            },
            {
              newValue: userAllowance.value,
              component_id: userAllowance.component_id,
              type: USER,
            },
          ],
        }),
      )
      setSuccessModal(true)
    } catch (error) {
      setPaymentFailed(true)
    }

    setUpdatingAllocateComponent(false)
  }

  const modalEnabled = billingProviderComponents.addons[ADDON_STORAGE]?.length

  const shouldFetchPricingChange =
    userAllowance.value !== currentUserAllowance ||
    trackAllowance.value !== currentTrackAllowance

  return (
    <ModalDialog
      width="large"
      title={modalEnabled ? 'Add Seats & Tracks' : 'Add Seats'}
      onCancel={onClose}
      primaryAction={{
        caption: 'Update Plan',
        onClick: onUpdatePlan,
        progressCaption: 'Loading',
        progressIndicator: updatingAllocateComponent,
        disabled: !hasUnsavedChanges || paymentFailed,
      }}
    >
      {loading || usersLoading ? (
        <Spinner />
      ) : (
        <>
          {modalEnabled ? <Heading size={500}>Seats</Heading> : null}
          <Stack>
            <OverQuotaAlert />

            <Label caption="Change seats" />
          </Stack>
          <div className="SeatsAndTracks_CustomDropdownWrapper">
            <CustomDropdown
              value={String(userAllowance.value)}
              options={usersOptions}
              onChange={option => {
                setUserAllowance({ ...option, type: userAllowance.type })
              }}
            />
            <span className="SeatsAndTracks_Description">
              Need a different amount?{' '}
              <ContactUsLink
                intercomMsg="I’d like to discuss upgrading the seats on my account"
                className="SeatsAndTracks_Link"
              >
                Contact us!
              </ContactUsLink>
            </span>
          </div>

          {modalEnabled && (
            <>
              <div className="SeatsAndTracks_Line" />
              <Heading size={500}>Tracks</Heading>
              {total_track_count > enterprise_track_allowance ? (
                <div className="SeatsAndTracks_AlertWrapper">
                  <Alert status="warning" icon="warning">
                    You have {enterprise_track_allowance} tracks on your{' '}
                    {currentBusinessPlan.name} plan, and are currently using{' '}
                    <span className="SeatsAndTracks_DangerText">
                      {total_track_count}
                    </span>{' '}
                    tracks. Please select a minimum of{' '}
                    {
                      tracksOptions.find(
                        ({ value }) => value >= total_track_count,
                      )?.value
                    }{' '}
                    tracks.
                  </Alert>
                </div>
              ) : (
                <p className="SeatsAndTracks_Description">
                  You’re using {total_track_count} of your current{' '}
                  {enterprise_track_allowance} tracks
                </p>
              )}
              <Label caption="Change tracks" />
              <div className="SeatsAndTracks_CustomDropdownWrapper">
                <CustomDropdown
                  value={String(trackAllowance.value)}
                  options={tracksOptions}
                  onChange={option => {
                    setTrackAllowance({ ...option, type: trackAllowance.type })
                  }}
                />
                <span className="SeatsAndTracks_Description">
                  Need a different amount?{' '}
                  <ContactUsLink
                    intercomMsg="I’d like to discuss upgrading the tracks on my account"
                    className="SeatsAndTracks_Link"
                  >
                    Contact us!
                  </ContactUsLink>
                </span>
              </div>
            </>
          )}

          <SeatsAndTracksChargebeePricingChange
            subscriptionPeriod={subscriptionPeriod}
            seatsQuantity={userAllowance.value}
            tracksQuantity={trackAllowance.value}
            currentUserAllowance={currentUserAllowance}
            currentTrackAllowance={currentTrackAllowance}
            shouldFetchPricingChange={shouldFetchPricingChange}
          />

          {paymentFailed && (
            <div className="SeatsAndTracks_AlertWrapper">
              <Alert status="danger" icon="warning">
                <div>Payment failed.</div>
                {updateBillingInfo}
              </Alert>
            </div>
          )}
        </>
      )}
    </ModalDialog>
  )
}

export default SeatsAndTracks
